'use client';

import { useEffect } from 'react';

/**
 * Compact hero (default). Hero je sticky; po odscrollovani sa zlozi na jeden
 * riadok: logo | search input (nizsi) | nav. Klasicky filter sa da otvorit aj
 * v zlozenom stave - lista sa natiahne o riadok fieldov. Scroll hore -> full hero.
 *
 * Hystereza: zloz pri scrollY >= fullH - compactH (obsah je vtedy pod hero presne tak, ze
 * toolbar sedi pod zlozenou listou - ziadna diera), rozloz pri scrollY < ten bod - 12.
 */
export function HeroCompact() {
    useEffect(() => {
        const hero = document.querySelector<HTMLElement>('.rx-hero');
        if (!hero) return;
        hero.classList.add('rx-hero--sticky');
        let compact = false;
        let transitioning = false; // 420ms morph medzi stavmi
        let settleTimer = 0;
        let raf = 0;
        let compactH = 72; // vyska zlozenej listy bez klasickych fieldov; zmeria sa po prvom zlozeni
        // --rx-bar-h: aktualna vyska listy (72 / 124 s filtrami) pre sticky toolbar vysledkov a mapu
        const root = document.documentElement;
        // spacer za hero drzi konstantnu vysku toku (fullH): ked sa hero zlozi o D px, spacer
        // o D px narastie -> obsah pod listou sa nepohne (ziadny skok pri zlozeni/rozlozeni)
        const spacer = document.createElement('div');
        spacer.className = 'rx-hero-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        hero.insertAdjacentElement('afterend', spacer);
        let fullH = hero.offsetHeight;
        const classicClosed = () => (hero.querySelector<HTMLElement>('.rx-hero-classic')?.offsetHeight ?? 0) === 0;
        const sync = () => {
            const h = hero.offsetHeight;
            // pocas morphu fullH nemenit, inak by spacer sledoval animaciu
            if (!compact && !transitioning) fullH = h;
            spacer.style.height = Math.max(0, fullH - h) + 'px';
            // aj pocas morphu = toolbar vysledkov sa vezie na spodnej hrane listy
            root.style.setProperty('--rx-bar-h', compact || transitioning ? h + 'px' : '0px');
            // toolbar vysledkov: ak je pod listou medzera (flow pozicia > spodok listy), natiahne sa
            // hore cez ::before o --rx-tb-gap - nikdy ziadna diera
            const tb = document.querySelector<HTMLElement>('.rx-results-toolbar');
            if (tb) {
                const gap = Math.max(0, Math.round(tb.getBoundingClientRect().top - hero.getBoundingClientRect().bottom));
                root.style.setProperty('--rx-tb-gap', gap + 'px');
            }
        };
        const ro = new ResizeObserver(sync);
        ro.observe(hero);
        /** rozlozenie: obsah pod listou nasleduje jej rastuci spodok (ziadna siva medzera), potom margin 0 */
        const glue = () => {
            spacer.style.marginTop = Math.min(0, Math.round(hero.getBoundingClientRect().bottom + window.scrollY - fullH)) + 'px';
            sync();
            raf = requestAnimationFrame(glue);
        };
        const settle = () => {
            transitioning = false;
            cancelAnimationFrame(raf);
            spacer.style.marginTop = '';
            // compactH len ked morph naozaj dobehol (hlavne vlakno moze byt zablokovane napr. Mapbox resize-om)
            const h = hero.offsetHeight;
            if (compact && classicClosed() && h < fullH / 2) compactH = h;
            sync();
        };
        /**
         * Hero je sticky vzdy (top 0), stavy sa menia morphom na mieste: input sa stiahne do riadku
         * s logom a nav, h1 a gauc fade, pilly sa zbalia. Prah zlozenia D = fullH - compactH: v tom
         * bode uz obsah odscrolloval pod hero presne o D px, takze toolbar vysledkov (flow = fullH)
         * je na viewport pozicii compactH - po zlozeni sedi priamo pod listou. Ziadny pas medzi
         * listou a obsahom, obsah sa nehybe (spacer drzi tok). Hystereza 12px proti flip-flopu.
         */
        const apply = () => {
            const y = window.scrollY;
            const d = Math.max(1, fullH - compactH);
            if (!compact && y >= d) compact = true;
            else if (compact && y < d - 12) compact = false;
            const next = compact ? '1' : '0';
            if (hero.dataset.compact === next) return;
            hero.dataset.compact = next;
            transitioning = true;
            cancelAnimationFrame(raf);
            window.clearTimeout(settleTimer);
            settleTimer = window.setTimeout(settle, 580);
            // pri rozlozeni rastie lista dole cez obsah - obsah ide s nou, nie za sivou medzerou
            if (!compact) raf = requestAnimationFrame(glue);
            sync();
            // TextareaAutosize drzi inline height - po zmene paddingu/fontu ju prepocita len na resize
            window.dispatchEvent(new Event('resize'));
            setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
        };
        apply();
        const onScroll = () => {
            apply();
            sync();
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(raf);
            window.clearTimeout(settleTimer);
            root.style.removeProperty('--rx-tb-gap');
            ro.disconnect();
            spacer.remove();
            root.style.removeProperty('--rx-bar-h');
            hero.classList.remove('rx-hero--sticky');
            delete hero.dataset.compact;
        };
    }, []);

    return (
        <style
            dangerouslySetInnerHTML={{
                __html: `
.rx-hero--sticky { position: sticky; top: 0; z-index: 1000; }
.rx-hero--sticky .rx-hero-couch,
.rx-hero--sticky .rx-hero-h1,
.rx-hero--sticky .rx-hero-h1m,
.rx-hero--sticky .rx-hero-tagline,
.rx-hero--sticky .rx-hero-pills { transition: opacity .32s var(--ease-rx), max-height .42s var(--ease-rx), margin .42s var(--ease-rx); }
.rx-hero--sticky .rx-hero-pills { max-height: 80px; overflow: visible; }
.rx-hero--sticky .rx-hero-inner { transition: padding .42s var(--ease-rx), gap .42s var(--ease-rx); }
.rx-hero--sticky .rx-hero-filter { transition: margin .42s var(--ease-rx); }
.rx-hero--sticky .rx-hero-card { transition: padding .42s var(--ease-rx), background-color .42s var(--ease-rx), border-color .42s var(--ease-rx), box-shadow .42s var(--ease-rx), max-width .42s var(--ease-rx), margin .42s var(--ease-rx), border-radius .42s var(--ease-rx); max-width: 100%; margin-left: auto; margin-right: auto; }
.rx-hero--sticky .rx-hero-nav { transition: translate .42s var(--ease-rx); }

.rx-hero[data-compact="1"] .rx-hero-couch { opacity: 0; }
.rx-hero[data-compact="1"] .rx-hero-h1,
.rx-hero[data-compact="1"] .rx-hero-tagline { opacity: 0; pointer-events: none; }
.rx-hero[data-compact="1"] .rx-hero-h1m { display: none; }
.rx-hero[data-compact="1"] .rx-hero-pills { max-height: 0; opacity: 0; overflow: hidden; pointer-events: none; }
.rx-hero[data-compact="1"] .rx-hero-inner { padding-top: 12px !important; padding-bottom: 12px !important; gap: 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-top { position: absolute; inset: 0; z-index: 3; pointer-events: none; display: flex; align-items: flex-start; padding-top: 12px; }
/* logo a nav drzia vysku input riadku (46px) aj ked sa pod nim otvoria klasicke fieldy */
.rx-hero[data-compact="1"] .rx-hero-logo, .rx-hero[data-compact="1"] .rx-hero-nav { height: 46px; display: flex; align-items: center; }
.rx-hero[data-compact="1"] .rx-hero-top > div { width: 100%; }
.rx-hero[data-compact="1"] .rx-hero-logo, .rx-hero[data-compact="1"] .rx-hero-nav { pointer-events: auto; }
.rx-hero[data-compact="1"] .rx-hero-nav { translate: 0 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-filter { margin-top: 0 !important; }
/* karta: bez glass - jej vlastny backdrop-filter vnoreny do rozmazanej listy kreslil tmavsi obdlznik */
.rx-hero[data-compact="1"] .rx-hero-card { padding: 0 252px 0 154px !important; background: transparent !important; border-color: transparent !important; box-shadow: none !important; margin-top: 0 !important; max-width: 100%; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }
/* input riadok vycentrovany medzi logom a nav, klasicke fieldy pod nim na plnu sirku */
.rx-hero--sticky .rx-hero-inputrow { transition: max-width .42s var(--ease-rx); max-width: 100%; margin-left: auto; margin-right: auto; }
.rx-hero[data-compact="1"] .rx-hero-inputrow { max-width: 700px; }
@media (min-width: 1536px) { .rx-hero[data-compact="1"] .rx-hero-inputrow { max-width: 760px; } }
/* klasicke fieldy sa vracaju na plnu sirku kontajnera (kompenzacia bocnych paddingov karty) */
.rx-hero[data-compact="1"] .rx-hero-classic { margin-left: -154px; margin-right: -252px; }
@media (max-width: 1279px) {
  .rx-hero[data-compact="1"] .rx-hero-card { padding-left: 144px !important; padding-right: 108px !important; }
  .rx-hero[data-compact="1"] .rx-hero-classic { margin-left: -144px; margin-right: -108px; }
}
/* nizsi input + mensi mic a toggle v zlozenom stave */
/* padding/font skacu okamzite - TextareaAutosize z nich pocita vysku pri resize evente; anmuje sa len height */
.rx-hero--sticky .rx-hero-plasma textarea { transition: height .42s var(--ease-rx); }
.rx-hero--sticky .rx-hero-inputrow > a, .rx-hero--sticky .rx-hero-plasma button[aria-pressed] { transition: height .42s var(--ease-rx), width .42s var(--ease-rx), min-width .42s var(--ease-rx), background-color .3s var(--ease-rx), transform .3s var(--ease-rx); }
.rx-hero[data-compact="1"] .rx-hero-plasma textarea { min-height: 46px !important; padding-top: 12px !important; padding-bottom: 12px !important; font-size: 14px !important; line-height: 22px !important; }
.rx-hero[data-compact="1"] .rx-hero-inputrow > a { height: 30px !important; width: 30px !important; min-width: 30px !important; }
/* ovladace v inpute: chat / send / VOX - 36px kruhy, 5px od hora, zdola aj sprava */
.rx-hero[data-compact="1"] .rx-hero-inputctl { align-items: center !important; padding-bottom: 0 !important; right: 5px !important; gap: 8px !important; }
/* chat ikonka o par px blizsie k sipke */
.rx-hero[data-compact="1"] .rx-hero-inputctl > button:first-of-type { margin-right: -4px !important; }
/* chat + send: 30px hover kruhy, mensie ikony, bez optickeho -ml (v 46px inpute uz netreba) */
.rx-hero[data-compact="1"] .rx-hero-inputctl > button { width: 30px !important; height: 30px !important; margin-left: 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-inputctl > button svg { width: 13px !important; height: 13px !important; }
/* sipka: 12px @ stroke 2.6 = 1.30px absolutne - rovnaka hrubka ako chat ikona (13px @ 2.4) */
.rx-hero[data-compact="1"] .rx-hero-inputctl > button:nth-of-type(2) svg { width: 12px !important; height: 12px !important; stroke-width: 2.6 !important; }
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed] { height: 36px !important; min-width: 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed="false"] { width: 36px !important; }
/* LIVE rozhovor v compact: VOX sa roztiahne na 142px (mute/repro), input mu uvolni miesto */
.rx-hero[data-compact="1"] .rx-hero-plasma:has(button[aria-pressed="true"]) textarea,
.rx-hero[data-compact="1"] .rx-hero-plasma:has(button[aria-pressed="true"]) .rx-hero-hint { padding-right: 236px !important; }
/* VOX v compact: ziadne hover roztiahnutie na pilulku (label ostava skryty, mala wave ostava) - inak sa obsah pretlacal cez 36px kruh */
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed]:not([aria-pressed="true"]) .voxl-label { max-width: 0 !important; opacity: 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed]:not([aria-pressed="true"]) .voxl-wave { width: 26px !important; }
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed]:not([aria-pressed="true"]) .voxl-bars-small { opacity: 1 !important; transition: color .3s var(--ease-rx), opacity .24s var(--ease-rx); }
/* hover: kruh je zlaty -> wave cierna, inak zanikne */
.rx-hero[data-compact="1"] .rx-hero-plasma button[aria-pressed]:not([aria-pressed="true"]):hover .voxl-bars-small { color: #0a0a0a; }
.rx-hero[data-compact="1"] .rx-hero-plasma textarea { padding-right: 130px !important; }
/* placeholder hint: mensi text, vertikalne na stred 46px inputu (overlay mal min-h 60) */
.rx-hero[data-compact="1"] .rx-hero-hint { min-height: 0 !important; height: 46px; padding-right: 130px !important; }
.rx-hero[data-compact="1"] .rx-hero-hint p { font-size: 14px !important; line-height: 22px !important; }
.rx-hero[data-compact="1"] .rx-hero-hint p.text-black\\/55 { font-size: 12px !important; }
/* klasicke fieldy: nizsie, rovnaky 12px odstup hore (od inputu) aj dole (od okraja listy) */
.rx-hero[data-compact="1"] .rx-hero-classic > div > div { margin-top: 12px !important; padding-bottom: 0 !important; }
.rx-hero[data-compact="1"] .rx-hero-classic .h-\\[40px\\], .rx-hero[data-compact="1"] .rx-hero-classic .md\\:h-\\[44px\\], .rx-hero[data-compact="1"] .rx-hero-classic .xl\\:h-\\[50px\\], .rx-hero[data-compact="1"] .rx-hero-classic .xl\\:h-\\[45px\\] { height: 38px !important; }
.rx-hero[data-compact="1"] .rx-hero-classic .xl\\:h-\\[42px\\], .rx-hero[data-compact="1"] .rx-hero-classic .xl\\:size-\\[42px\\], .rx-hero[data-compact="1"] .rx-hero-classic .md\\:h-\\[35px\\] { height: 38px !important; width: auto; }
.rx-hero[data-compact="1"] .rx-hero-classic .xl\\:size-\\[42px\\] { width: 38px !important; min-width: 38px !important; }
/* vnutorny mt-[25px] kolabuje s 12px obalu na 25 - v compact ho nulujem, ostane presne 12 hore aj dole */
.rx-hero[data-compact="1"] .rx-hero-classic .mt-\\[11px\\] { margin-top: 0 !important; }
/* pozadie: gradient (full hero) a zlate sklo (compact) su dve vrstvy, ktore sa crossfaduju cez opacity -
   gradient -> farba sa nedaju interpolovat a prepinali by preblikom */
.rx-hero--sticky { background: transparent !important; }
.rx-hero--sticky::before, .rx-hero--sticky::after { content: ""; position: absolute; inset: 0; z-index: -1; pointer-events: none; transition: opacity .42s var(--ease-rx); }
.rx-hero--sticky::before { background: linear-gradient(180deg, #daa104 0%, #eaba05 100%); opacity: 1; }
.rx-hero--sticky::after { background: rgba(226,176,3,.92); opacity: 0; }
.rx-hero[data-compact="1"]::before { opacity: 0; }
.rx-hero[data-compact="1"]::after { opacity: 1; }
.rx-hero[data-compact="1"] { backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); }
/* sticky toolbar vysledkov: ::before natiahne pozadie hore o --rx-tb-gap (medzera pod listou pocas prechodu) */
.rx-results-toolbar::before { content: ""; position: absolute; left: 0; right: 0; bottom: 100%; height: var(--rx-tb-gap, 0px); background: inherit; pointer-events: none; }
/* scroll anchoring vypnut - rast ::before nad toolbarom a zmena vysky hero/spacera by inak Chrome "kompenzoval" posunom scrollu */
html, body, .rx-hero--sticky, .rx-hero-spacer, .rx-results-toolbar { overflow-anchor: none; }
/* sticky toolbar vysledkov: v zlozenom stave oddelit od kariet jemnym tienom + hairline */
.rx-hero[data-compact="1"] ~ div .rx-results-toolbar { box-shadow: 0 12px 28px -18px rgba(0,0,0,.28), 0 1px 0 rgba(0,0,0,.05); border-bottom-color: rgba(0,0,0,.06); }
/* pod xl: Registruj / Prihlas len ako ikony v kruhoch, input row uzsi, aby nekolidoval s logom a nav */
@media (max-width: 1279px) {
  .rx-hero[data-compact="1"] .rx-hero-nav a { width: 38px; height: 38px; padding: 0 !important; justify-content: center; gap: 0; background: #F6D886; }
  .rx-hero[data-compact="1"] .rx-hero-nav a:hover { background: #FBE7AE; color: #111; }
  .rx-hero[data-compact="1"] .rx-hero-nav a p { display: none !important; }
  .rx-hero[data-compact="1"] .rx-hero-nav { gap: 8px; }
}
@media (max-width: 1023px) { .rx-hero[data-compact="1"] .rx-hero-inputrow { max-width: 460px; } }
@media (max-width: 767px) {
  .rx-hero[data-compact="1"] .rx-hero-card { padding-left: 0 !important; padding-right: 0 !important; }
  .rx-hero[data-compact="1"] .rx-hero-classic { margin-left: 0; margin-right: 0; }
}
/* mobil: logo vzdy vlavo, nav skryty, input zarovnany doprava vedla loga */
@media (max-width: 767px) {
  .rx-hero[data-compact="1"] .rx-hero-nav { display: none !important; }
  .rx-hero[data-compact="1"] .rx-hero-logo { height: 46px; }
  .rx-hero[data-compact="1"] .rx-hero-inputrow { max-width: calc(100% - 112px); margin-left: auto; margin-right: 0; }
  /* na mobile v inpute len VOX kruh - chat a send by zobrali polovicu sirky */
  .rx-hero[data-compact="1"] .rx-hero-inputctl > button { display: none !important; }
  .rx-hero[data-compact="1"] .rx-hero-plasma textarea,
  .rx-hero[data-compact="1"] .rx-hero-hint { padding-right: 50px !important; }
}
`,
            }}
        />
    );
}
